import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { getAuthedToken } from "../../auth/createAuthedAxios.ts";
import { load } from '@loaders.gl/core';
import { setRTLTextPlugin } from 'maplibre-gl';
import { point, distance, bearing } from '@turf/turf';
import { useTheme } from '@mui/material/styles';
import useActive from "../../hooks/useActive";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../theme/Loader"
import NoDataMessage from "../NoDataMessage";
import { Map, useControl } from 'react-map-gl/maplibre';
import { MapboxOverlay } from '@deck.gl/mapbox';
import {
  IconLayer,
  TextLayer,

} from '@deck.gl/layers';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { MVTLayer } from '@deck.gl/geo-layers';
import * as d3 from "d3";
import { niceColorFromInt } from "../../utils/ColorUtils"
import RealTimeProgress from "./RealTimeProgress";
import ControlPanel from "./ControlPanel";
import {

  setVizParam,
  setDisplayDate,
  setCurrentStop,
  setCurrentLines,

} from "../../reducers/RealTimeParamSlice";

import {
  REALTIME_TRIPS_RESET,
  REALTIME_SPEED_RESET,
} from "../../constants/realtimeConstants";
import {
  getRealTimeTrips,
  getRealTimeSpeed,
} from "../../actions/realtimeActions";

import {
  getRouteNames,
  getStopDetails,
  getRouteDetails
} from "../../actions/gtfsActions";


function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

// Layers holding the moving vehicles and their line-number labels.
const VEHICLE_LAYER_IDS = ['trips', 'text-layer'];
const NETWORK_LAYER_ID = 'network-mvt';

function withAccessToken(url, token) {
  const u = new URL(url, window.location.href);
  u.searchParams.set('access_token', token);
  return u.toString();
}


const RealTimeMap = (props) => {


  const layersRef = useRef([]);
  const theme = useTheme()


  // MVT tiles fan out heavily during zoom. Put the token in the tile URL so
  // tile GETs remain simple requests and do not trigger an OPTIONS preflight
  // for every tile.
  const fetchTileWithAuth = useCallback(
    async (url, { layer, loadOptions, signal }) => {
      const buildOptions = () => ({
        ...loadOptions,
        fetch: {
          ...(loadOptions?.fetch && typeof loadOptions.fetch === 'object' ? loadOptions.fetch : {}),
          signal,
        },
      });

      const token = await getAuthedToken(false, true);
      if (!token) {
        const freshToken = await getAuthedToken(true, true);
        if (!freshToken) {
          throw new Error('Missing Clerk token for authenticated tile request');
        }
        return load(withAccessToken(url, freshToken), layer.props.loaders, buildOptions());
      }

      try {
        return await load(withAccessToken(url, token), layer.props.loaders, buildOptions());
      } catch (err) {
        const status = err?.status ?? err?.response?.status;
        const is401 = status === 401 || /\b401\b/.test(err?.message ?? '');
        if (is401) {
          const freshToken = await getAuthedToken(true, true);
          if (freshToken) {
            return load(withAccessToken(url, freshToken), layer.props.loaders, buildOptions());
          }
        }
        throw err;
      }
    },
    []
  );

  const currLang = useSelector((state) => state.lang.value.currLang);
  const pageDir = useSelector((state) => state.lang.value.pageDir);
  const transitDataDict = useSelector((state) => state.lang.value.transitDataDict);
  const roadSectionPopup = useSelector((state) => state.lang.value.roadSectionPopup);
  const realtimeTrips = useSelector((state) => state.realtimeTrips);
  const realtimeSpeed = useSelector((state) => state.realtimeSpeed);
  const routeNames = useSelector((state) => state.routeNames);
  const stopDetails = useSelector((state) => state.stopDetails);
  const routeDetails = useSelector((state) => state.routeDetails);
  

  const realtime = useSelector((state) => state.realtime.value);
  const tripsKeysRef = useSelector((state) => state.realtime.value.tripsKeysRef);
  const stopDetailsKeyRef = useSelector((state) => state.realtime.value.stopDetailsKeyRef);
  const version = 1;

  const dispatch = useDispatch();
  const initialViewState = useMemo(() => {
    return {
      longitude: 34.77683,
      latitude: 32.05,
      zoom: 11,
      pitch: 0,
      bearing: 0
    }
  }, []);


  const screenRef = useRef(null);


  const [endEpoch, setEndEpoch] = useState(new Date().getTime());
  const [mapLocation, setMapLocation] = useState(new Date().getTime());
  const active = useActive(15000);

  const [pointData, setPointData] = useState([[], null, realtime.locname]);
  const mapRef = useRef(null);
  const [hoverInfo, setHoverInfo] = useState();
  const [stopHoverInfo, setStopHoverInfo] = useState();
  const [speedTilesIdxs, setSpeedTilesIdxs] = useState([]);
  const speedTilesIdxsRef = useRef([]);
  const speedTilesKeyRef = useRef("");
  const speedRefreshTimeoutRef = useRef(null);
  const [liveDataVersion, setLiveDataVersion] = useState(0);
  const [routeHoverInfo, setRouteHoverInfo] = useState();
  const [edgeHoverInfo, setEdgeHoverInfo] = useState();
  const [updatedData, setUpdatedData] = useState([[], null, realtime.locname])
  const dataInterval = useRef(null)
  const animationInterval = useRef(null)
  const refreshRate = useMemo(() => { return 750 },
    [])

  const staleTimeSeconds = useMemo(() => { return 120 },
    [])


  const animationCounter = useRef(0)
  const animationCounterInterval = useRef(realtime.currentDate ? realtime.timeInterval[realtime.locale] * (1000 / refreshRate) :
    (realtime.timeInterval[realtime.locale]) * (1000 / refreshRate))

  function intToRGB(num) {
    // Ensure it's a 32-bit integer
    let hash = num | 0;

    // Simple mixing (can tweak constants for different distributions)
    hash = ((hash >> 16) ^ hash) * 0x45d9f3b;
    hash = ((hash >> 16) ^ hash) * 0x45d9f3b;
    hash = (hash >> 16) ^ hash;

    // Use lower 24 bits as RGB
    const r = (hash >> 16) & 0xFF;
    const g = (hash >> 8) & 0xFF;
    const b = hash & 0xFF;

    return [r, g, b];
  }

  function colorIsDark(c) {
    let color = (c.charAt(0) === '#') ? c.substring(1, 7) : c;
    let r = parseInt(color.substring(0, 2), 16);
    let g = parseInt(color.substring(2, 4), 16);
    let b = parseInt(color.substring(4, 6), 16);
    return ((r * 0.299) + (g * 0.587) + (b * 0.114)) <= 186;
  }

  const cyrb53 = (str, seed = 0) => {
    let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  };


  const colorScaleCars = useRef(d3.scaleLinear()
    .domain([0, 10, 25,
      50,
      75,
      120,
      3000])
    .range([
      '#d62728',
      '#ffcc00',
      "#2ca02c",
      '#6baed6',
      '#1f77b4',
      '#1f77b4',
    ])
  )


     const colorScale = useRef(d3.scaleLinear()
    .domain([0, 10, 25,
      50,
      75,
      120,
      3000])
       .range([
      '#d62728',
      '#ffcc00',
      "#2ca02c",
      '#6baed6',
      '#1f77b4',
      '#1f77b4',
    ])
    // .range([
    //   '#e47273',
    //   '#ffde59',
    //   "#76c176",
    //   '#9fcae4',
    //   '#6da6ce',
    //   '#6da6ce',
    // ])
  )
  const colorScaleBearing = useRef(d3.scaleSequential([0, 360], d3.interpolateRainbow))
  const sizeScale = useRef(d3.scaleLinear([0, 10, 14, 18], [2, 15, 30, 45]))
  const fadeColor = useRef(d3.interpolateNumber(255, 0))
  const widthScale = useRef(d3.scaleLinear([0, 5, 10, 20,], [5, 10, 15, 20]))

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      screenRef.current.requestFullscreen().catch((err) => {
        console.error(`Error trying to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  }


  const isInside = (pt, lonExtent, latExtent) => {
    return pt[0] >= lonExtent[0] && pt[0] <= lonExtent[1] &&
      pt[1] >= latExtent[0] && pt[1] <= latExtent[1]
  }

  const handleSelectStop = (obj) => {
    if (Object.keys(obj.properties).length > 0) {
      dispatch(setCurrentStop(obj.properties))
      dispatch(getStopDetails(obj.properties.stop_code))


    }

  }

  useEffect(() => {

    dispatch(getRouteNames())

  }, []);


    function resetStopSelection(e) {
      e.preventDefault();
      dispatch(setCurrentStop(null))
      dispatch(setCurrentLines(null))
    
    }


  useEffect(() => {
    if (stopDetails.data.length > 0) {

      dispatch(setCurrentLines(stopDetails.data[0][stopDetailsKeyRef.route_ids]))
    }


  }, [stopDetails.data, stopDetailsKeyRef]);


  useEffect(() => {
    dataInterval.current = null;
    return () => clearInterval(dataInterval.current);
  }, []);


  useEffect(() => {
    animationInterval.current = null;
    return () => clearInterval(animationInterval.current);
  }, []);


  useEffect(() => {
    mapRef.current = null;
  }, []);


  // useEffect(() => {
  //   if (realtime.currentLines) {
  //     dispatch(setVizParam("trip"))

  //   }

  // }, [realtime.currentLines, dispatch]);


  useEffect(() => {
    if (
      realtimeTrips.data &&
      realtimeTrips.data.length > 0 &&
      realtimeTrips.data.length === 0) {
      layersRef.current = [


      ]

    }
  }, [realtimeTrips.data,



  realtime.currentLines,
  ]);


  useEffect(() => {
    if (mapRef.current && realtimeTrips.data && realtimeTrips.data.length > 0
      && realtimeTrips.data.length > 0
    ) {
      const lonExt = d3.extent(realtimeTrips.data.map(d => d[tripsKeysRef.longitude]))
      const latExt = d3.extent(realtimeTrips.data.map(d => d[tripsKeysRef.latitude]))
      if (lonExt !== null && latExt !== null) {
        mapRef.current.fitBounds(
          [
            [lonExt[0], latExt[0]],
            [lonExt[1], latExt[1]]
          ],
          { padding: 40, duration: 3000 }
        );
      }
    }
  }, [mapLocation,
    tripsKeysRef.latitude,
    tripsKeysRef.longitude,
  ]);

  // useEffect for fitting the map to the selected route distance
  useEffect(() => {
    if (mapRef.current && realtimeTrips.data && realtimeTrips.data.length > 0
      && realtimeTrips.data.length > 0
    ) {
      const lonExt = d3.extent(realtimeTrips.data.map(d => d[tripsKeysRef.longitude]))
      const latExt = d3.extent(realtimeTrips.data.map(d => d[tripsKeysRef.latitude]))
      if (lonExt !== null && latExt !== null) {
        mapRef.current.fitBounds(
          [
            [lonExt[0], latExt[0]],
            [lonExt[1], latExt[1]]
          ],
          { padding: 40, duration: 3000 }
        );
      }
    }
  }, [
    tripsKeysRef.latitude,
    tripsKeysRef.longitude,
  ]);


  const resetData = useCallback(() => {

    dispatch({ type: REALTIME_TRIPS_RESET })

    setPointData([[], null, realtime.locname])
    setUpdatedData([[], null, realtime.locname])
    layersRef.current = []
  }, [realtime.locname, dispatch])


  useEffect(() => {
    if (active) {
      dispatch(getRealTimeTrips());
    }
    else {
      dispatch({ type: REALTIME_TRIPS_RESET })
      dispatch({ type: REALTIME_SPEED_RESET })
      layersRef.current = []
      speedTilesIdxsRef.current = []
      speedTilesKeyRef.current = ""
      setSpeedTilesIdxs([])
      setUpdatedData([[], null, realtime.locname])
      setPointData([[], null, realtime.locname])

    }
  }, [active]);

  useEffect(() => {
    if (!active || speedTilesIdxs.length === 0) {
      return;
    }

    speedRefreshTimeoutRef.current = setTimeout(() => {
      dispatch(getRealTimeSpeed(speedTilesIdxs));
      setLiveDataVersion((v) => v + 1);
    }, 350);

    return () => clearTimeout(speedRefreshTimeoutRef.current);
  }, [
    active,
    dispatch,
    speedTilesIdxs,
  ]);

  useEffect(() => {
    if (!active) {
      return;
    }

    const interval = setInterval(() => {
      if (speedTilesIdxsRef.current.length === 0) return;
      dispatch(getRealTimeSpeed(speedTilesIdxsRef.current));
      setLiveDataVersion((v) => v + 1);
    }, 30 * 1000);

    return () => clearInterval(interval);
  }, [active, dispatch]);

  useEffect(() => {
    if (active && realtime.locname) {
      let count = 0


      if (realtime.currentDate) {
        dataInterval.current = setInterval(() => {
          count += 1;

          const now = new Date().getTime();

          if (realtime.locale === "Israel") {
            dispatch(getRealTimeTrips());

          }

          setEndEpoch(now + realtime.timeInterval[realtime.locale] * 1000);
          dispatch(setDisplayDate(now));
        }, realtime.timeInterval[realtime.locale] * 1000);
        return () => clearInterval(dataInterval.current);
      }


    } else {
      return () => clearInterval(dataInterval.current);
    }
  }, [
    active,
    dispatch,
    realtime.limitDates,
    realtime.currentDate,
    realtime.locname,
    realtime.locale,
    realtime.timeInterval,
    realtime.currentLines,
  ]);



  const updateAnimationLayers = useCallback(() => {



    if (mapRef.current && active && updatedData && updatedData[0].length > 0
      && (typeof updatedData[0][0][tripsKeysRef.interpolateVelocity] === 'function')
    ) {
      const step = animationCounter.current / animationCounterInterval.current
      let now = new Date().getTime() / 1000
      now = now - now % 30

      const zoom = mapRef.current.getZoom()
  

      const bounds = mapRef.current.getBounds()

      const lonExtent = d3.extent([bounds._ne.lng, bounds._sw.lng])
      const latExtent = d3.extent([bounds._ne.lat, bounds._sw.lat])
      let tmpPointData = []
      updatedData[0].forEach((d) => {

        let valid = (isInside(d[tripsKeysRef.position], lonExtent, latExtent) &&
          (realtime.currentLines === null || realtime.currentLines.includes(d[tripsKeysRef.lineref])))


        if (d[tripsKeysRef.move]) {
          const currVelocity = d[tripsKeysRef.interpolateVelocity](step)
          const currBearing = d[tripsKeysRef.interpolateBearing](step)
          let color = "[0, 0, 0, 0]"
          color = realtime.vizParam === "trip"
            ? cyrb53(d[tripsKeysRef.datedvehiclejourneyref]) % 10

            : realtime.vizParam === "velocity"
              ? colorScaleCars.current(currVelocity).replace("rgb(", "[").replace(")", "]")
              : realtime.vizParam === "bearing"
                ? colorScaleBearing.current(currBearing).replace("rgb(", "[").replace(")", "]")
                : "[" + realtime.operatorColors[d[tripsKeysRef.operatorref]].match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)).toString() + "]"
          if (d[tripsKeysRef.stage] === "end") {
            color = color.replace("]", ", " + Math.max(0, (parseInt(fadeColor.current(step)))).toString() + "]")
          }
          else if (d[tripsKeysRef.stage] === "start") {
            color = color.replace("]", ", " + Math.min(255, parseInt(
              fadeColor.current((animationCounterInterval.current - 10 -
                animationCounter.current) / animationCounterInterval.current))).toString() + "]")
          }
          else {
            color = color.replace("]", ", 255]")
          }


          let ps = [...d]
          ps[tripsKeysRef.currentVelocity] = currVelocity
          ps[tripsKeysRef.currentBearing] = currBearing
          ps[tripsKeysRef.color] = color
          ps[tripsKeysRef.valid] = valid
          ps[tripsKeysRef.position] = [d[tripsKeysRef.interpolateLon](step), d[tripsKeysRef.interpolateLat](step)]
          tmpPointData.push(ps)

        }
        else {
          let color = "[0, 0, 0, 0]"
          color = realtime.vizParam === "trip"
            ? cyrb53(d[tripsKeysRef.datedvehiclejourneyref]) % 10

            : realtime.vizParam === "velocity"
              ? colorScaleCars.current(d[tripsKeysRef.currentVelocity]).replace("rgb(", "[").replace(")", "]")
              : realtime.vizParam === "bearing"
                ? colorScaleBearing.current(d[tripsKeysRef.currentBearing]).replace("rgb(", "[").replace(")", "]")
                : "[" + realtime.operatorColors[d[tripsKeysRef.operatorref]].match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)).toString() + "]"
          if (d[tripsKeysRef.stage] === "end") {
            color = color.replace("]", ", " + Math.max(0, (parseInt(fadeColor.current(step)))).toString() + "]")
          }
          else if (d[tripsKeysRef.stage] === "start") {
            color = color.replace("]", ", " + Math.min(255, parseInt(
              fadeColor.current((animationCounterInterval.current - 10 -
                animationCounter.current) / animationCounterInterval.current))).toString() + "]")
          }
          else {
            color = color.replace("]", ", 255]")
          }
          let ps = [...d]
          ps[tripsKeysRef.color] = color
          ps[tripsKeysRef.valid] = valid
          tmpPointData.push(ps)
        }
      })
      setPointData([tmpPointData, updatedData[1], updatedData[2]])

      tmpPointData = tmpPointData.filter(d => d[tripsKeysRef.valid])
      layersRef.current = [


        (realtime.currentStop === null &&
          new MVTLayer({ id: NETWORK_LAYER_ID,

            data: process.env.REACT_APP_HOST_IP + `network/edges/{z}/{x}/{y}.mvt`,
            binary: true,
            debounceTime: 0,
            maxRequests: 6,
            // maxCacheSize: 0,
            tileSize: 512,

            // maxFeatures: 200000,
            lineWidthUnits: "pixels",
            pickable: true,
            getLineWidth: d => {
        
              const width = realtimeSpeed.data[d.properties.edge_index]

              if (width && width[1] != 'null') {
                const w = parseInt(width[1])
                const c = widthScale.current(w)


                return c
              }
              else {

                return 5
              }

            },
            getLineColor: (d) => {
          
              const speed = realtimeSpeed.data[d.properties.edge_index]

              if (speed && speed[0] != 'null') {
                const s = Math.min(120, parseInt(speed[0]))
                const c = JSON.parse(colorScale.current(parseInt(s)).replace("rgb(", "[").replace(")", ", 200]"))

                return c
              }
              else {

                return [150, 150, 150, 150]
              }

            },
            updateTriggers: {
              getLineColor: [liveDataVersion],
              getLineWidth: [liveDataVersion]          // add any accessor that depends on live data
            },
            onViewportLoad: (tiles) => {
              const idxs = tiles.map((t) => {
                return {
                  x: t.index.x,
                  y: t.index.y,
                  z: t.index.z,
                }
              })
              const key = idxs
                .map((t) => `${t.z}/${t.x}/${t.y}`)
                .sort()
                .join('|');
              if (key === speedTilesKeyRef.current) return;
              speedTilesKeyRef.current = key;
              speedTilesIdxsRef.current = idxs;
              setSpeedTilesIdxs(idxs)
            },
            // miterLimit:4,
            getFillColor: [0, 0, 0, 0], // not used
            minZoom: 0,
            maxZoom: 30,
            onClick: ({ object }) => {
              if (!object) return;



            },
            onHover: (info) => {
              setEdgeHoverInfo(info);
            },
            fetch: fetchTileWithAuth,
          })),

        (realtime.currentStop && realtime.currentStop.hash_shapes && 
          new MVTLayer({ id: 'routes-mvt',

          data: process.env.REACT_APP_HOST_IP + `stop-routes/${realtime.currentStop.hash_shapes}/{z}/{x}/{y}.mvt`,
          binary: true,
          pickable: true,
          lineWidthUnits: "pixels",
          getLineWidth: d => 5,
          getLineColor: (d) => {
            // const c = niceColorFromInt(d.properties.route_id,  {preset:"muted"
            //   // , saturation:50, lightness:50
            // })
            const c = realtime.operatorColors[routeNames.data[d.properties.route_id][realtime.routeNamesKeyRef.operatorref]]

            return JSON.parse("["+c.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)).toString() + ", 150]")
          },
          // miterLimit:4,
          getFillColor: [0, 0, 0, 0], // not used
          minZoom: 0,
          maxZoom: 24,
          onClick: ({ object }) => {
            if (!object) return;
            const { route_id } = object.properties;
        


          },
          onHover: (info) => {
            setRouteHoverInfo(info);
          },
          fetch: fetchTileWithAuth,
        })),


        new MVTLayer({id: 'stops-mvt',
          binary: false,
          minZoom: 14,
          maxZoom: 24,
          debounceTime: 1000,
          maxRequests: 1,
          tileSize: 512,
          pickable: true,
          onClick: ({ object }) => handleSelectStop(object),
          onHover: (info) => {
            setStopHoverInfo(info);
          },
          getPosition: d => d.geometry.coordinates,
          data: process.env.REACT_APP_HOST_IP + `stops/{z}/{x}/{y}.mvt`,
          fetch: fetchTileWithAuth,
          renderSubLayers: props => {
            return new IconLayer(props, {
              minZoom: 14,
              maxZoom: 24,
              pickable: true,
              onClick: ({ object }) => {
                // if (!object) return;

          

              },
              // onHover: info => setStopHoverInfo(info),
              getIcon: (d) => 'stop-icon',
              getSize: d => 20,
              getColor:
                d => [
                  0,
                  140,
                  186
                ],
              opacity: 0.5,
              iconAtlas: process.env.REACT_APP_STATICFILES_URL + "bus_stop_with_pole_128.png",
              iconMapping: {
                'stop-icon': {
                  "x": 0,
                  "y": 0,
                  "width": 128,
                  "height": 128,
                  "anchorY": 64,
                  "anchorX": 65,
                  "mask": false
                },
              },
            });
          },
        }),


        new IconLayer({ id: `trips`,
          pickable: zoom <= 15 ? true : false,
          onHover: info => setHoverInfo(info),
          // onClick: info => handleSelectLines(info),
          data: tmpPointData,
          billboard: true,
          // background: true,

          // outlineColor: [255, 255, 255, 0],
          getPosition: d => d[tripsKeysRef.position],
          getColor: (d) => {
            return JSON.parse(d[tripsKeysRef.color])
          },
       
          getIcon: (d) => {

            if (d[tripsKeysRef.operatorref] === 2) { return "train-nini" }
            if (d[tripsKeysRef.operatorref] === 22 || d[tripsKeysRef.operatorref] === 20 || d[tripsKeysRef.operatorref] === 21) { return "light-rail-nini" }
            else {
              return 'bus-nini'
            }
            // else if  ([34446, 34447, 34448, 34449, 34450].includes(d[tripsKeysRef.lineref]) {
            //   return 'light-rail'
            // }
            // else {
            //   return "bus"
            // }

            // if (d[tripsKeysRef.currentBearing] > 180) {
            //   return 'bus1'
            // }
            // else {
            //   return "bus-flip1"
            // }

          },
          getColor: (d) => {
            return JSON.parse(d[tripsKeysRef.color])
          },
          getAngle: d => 360 - d[tripsKeysRef.currentBearing],
          getSize: d => {

            if (d[tripsKeysRef.operatorref] === 2) { return sizeScale.current(zoom) * 2.5 }
            if (d[tripsKeysRef.operatorref] === 22 || d[tripsKeysRef.operatorref] === 20 || d[tripsKeysRef.operatorref] === 21) { return sizeScale.current(zoom) * 2.5 }
            else {
              return sizeScale.current(zoom) * 1.5
            }

          },
          iconAtlas: process.env.REACT_APP_STATICFILES_URL + 'transit-icons-nini.png',
          iconMapping: {
             "bus-nini": {
              "x": 295,
              "y": 0,
              "height": 202,
              "width": 106,
              "anchorY": 101,
              "anchorX": 53,
              "mask": true
            },

            "train-nini": {
              "x": 406,
              "y": 0,
              "height": 432,
              "width": 78,
              "anchorY": 216,
              "anchorX": 39,
              "mask": true
            },

            "light-rail-nini": {
              "x": 6,
              "y": 0,
              "height": 346,
              "width": 78,
              "anchorY": 173,
              "anchorX": 39,
              "mask": true
            },

            "bus": {
              "x": 280,
              "y": 0,
              "height": 410,
              "width": 160,
              "anchorY": 240,
              "anchorX": 80,
              "mask": true
            },
             "bus2": {
              "x": 1020,
              "y": 0,
              "height": 424,
              "width": 202,
              "anchorY": 212,
              "anchorX": 101,
              "mask": true
            },

            "train": {
              "x": 460,
              "y": 0,
              "height": 720,
              "width": 160,
              "anchorY": 450,
              "anchorX": 80,
              "mask": true
            },

            "light-rail": {
              "x": 650,
              "y": 0,
              "height": 720,
              "width": 160,
              "anchorY": 345,
              "anchorX": 80,
              "mask": true
            },


            "bus1": {
              "x": 164,
              "y": 44,
              "height": 330,
              "width": 200,
              "anchorY": 165,
              "anchorX": 110,
              "mask": true
            },

            "bus-flip1": {
              "x": 674,
              "y": 1580,
              "height": 330,
              "width": 200,
              "anchorY": 165,
              "anchorX": 70,
              "mask": true
            },

          },

        },

        ),
        new TextLayer({ id: `text-layer`,
          data: zoom <= 10 ? [] : tmpPointData,
          pickable: true,
          onHover: info => setHoverInfo(info),
          // onClick: info => handleSelectLines(info),
          getPosition: (d) => d[tripsKeysRef.position],
          getText: (d) => {
            // console.log(d[tripsKeysRef.route_short_name])
           return  d[tripsKeysRef.route_short_name].length >= 2 ?
            d[tripsKeysRef.route_short_name]
            : ` ${d[tripsKeysRef.route_short_name]} `
          },
          getAlignmentBaseline: 'center',
          getBackgroundColor: d => {
            
              const c = realtime.vizParam === "velocity" ?
              JSON.parse("[" + realtime.operatorColors[d[tripsKeysRef.operatorref]].match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)).toString() + "]")
              : JSON.parse(colorScaleCars.current(d[tripsKeysRef.currentVelocity]).replace("rgb(", "[").replace(")", "]"))
            if (d[tripsKeysRef.stage] === "end") {
              return [...c, Math.max(0, (parseInt(fadeColor.current(step))))]
            }
            else if (d[tripsKeysRef.stage] === "start") {
              return [...c, Math.min(255, parseInt(fadeColor.current((animationCounterInterval.current
                - 10 - animationCounter.current) / animationCounterInterval.current)))]
            }
            else {
              return [...c, 200]
            }
          },
          getLineWidth: 0,
          getSize: d => {
            if (zoom <= 12) {
              return 10
            }
            if (zoom <= 13) {
              return 11
            }
            else if (zoom <= 14) {
              return 12
            }
            else if (zoom <= 14) {
              return 13
            }
            else {
              return 14
            }
          },
          fontWeight: "bold",
          getTextAnchor: 'middle',
          background: true,
          fontSettings: { sdf: true, },
          outlineColor: [255, 255, 255, 0],
          backgroundBorderRadius: 5,
          getColor: d => {
            let c = realtime.operatorColors[d[tripsKeysRef.operatorref]]
            if (c===undefined){
              c = "#ffffff"
            }
         
            const dark = colorIsDark(c)
            if (zoom <= 10 || !d[tripsKeysRef.valid]) {

              return [255, 255, 255, 0]
            }
            else if (d[tripsKeysRef.stage] === "end") {
              if (dark) {
                return [255, 255, 255, Math.max(0, (parseInt(fadeColor.current(step))))]
              }
              else { return [50, 50, 50, Math.max(0, (parseInt(fadeColor.current(step))))] }


            }
            else if (d[tripsKeysRef.stage] === "start") {
              if (dark) {
                return [255, 255, 255, Math.min(255, parseInt(fadeColor.current((animationCounterInterval.current -
                  10 - animationCounter.current) / animationCounterInterval.current)))]
              }
              else {
                return [50, 50, 50, Math.min(255, parseInt(fadeColor.current((animationCounterInterval.current -
                  10 - animationCounter.current) / animationCounterInterval.current)))]

              }
            }
            else {
              if (dark) {
                return [255, 255, 255]
              }
              else {

                return [50, 50, 50]
              }
            }
          },
          outlineWidth: 0,
          getAngle: d => d[tripsKeysRef.currentBearing] > 180 ? 360 - d[tripsKeysRef.currentBearing] - 90
            : 180 - d[tripsKeysRef.currentBearing] - 90,
        }),
      ]
    }
    else if (mapRef.current && active) {
      layersRef.current = [

      ]
    }
    if (animationCounter.current < animationCounterInterval.current) {
      animationCounter.current += 1
    }
  }, [
    realtime.locale,
    realtime.locname,
    realtime.showTrafficLights,
    realtime.currentDate,

    realtime.trafficLightsSliderVal,
    updatedData,
    animationCounter,
    animationCounterInterval,
    active,
    realtime.currentLines,
    realtime.currentOperators,
    realtime.speedSliderVal,
    realtime.vizParam,
    realtime.currentStop,
    realtime.operatorColors,
    realtimeSpeed.data,
    liveDataVersion,
    tripsKeysRef,


  ])


  useEffect(() => {
    if (active) {
      animationInterval.current = setInterval(() => {

        if (animationCounter.current <= animationCounterInterval.current) {
          updateAnimationLayers()
        }
      }, refreshRate);
      return () => clearInterval(animationInterval.current);
    }
  }, [
    refreshRate,
    updateAnimationLayers,
    active
  ]);


  useEffect(() => {
    resetData()
    if (!realtime.currentDate) {
      setEndEpoch(realtime.limitDates[0] + realtime.timeInterval[realtime.locale] * 1000);
      dispatch(setDisplayDate(realtime.limitDates[0]))
    }
    else {
      const now = new Date().getTime()
      setEndEpoch(now + realtime.timeInterval[realtime.locale] * 1000);
      dispatch(setDisplayDate(now))
    }
  }, [
    realtime.limitDates, realtime.currentDate, realtime.locname, dispatch, resetData,
    realtime.timeInterval, tripsKeysRef, realtime.locale
  ]);

  useEffect(() => {
    if (active && realtimeTrips.data && realtimeTrips.data.length > 0
      && routeNames.data && Object.keys(routeNames.data).length >0

    ) {


      animationCounterInterval.current = (realtime.currentDate ? realtime.timeInterval[realtime.locale] * (1000 / refreshRate) :
        (realtime.timeInterval[realtime.locale]) * (1000 / refreshRate))

      const now = realtime.currentDate ? new Date().getTime() / 1000 : endEpoch;
      let newData = []
      const loc = realtime.locale
      const time = now
      let ids = []
      if (pointData[0].length > 0
      ) {
        ids = pointData[0].map(d => d[tripsKeysRef.datedvehiclejourneyref])
        const step = 1
        pointData[0].forEach((item) => {

        

          let d = [...item]
         
          d[tripsKeysRef.currentVelocity] = d[tripsKeysRef.interpolateVelocity](step)
          d[tripsKeysRef.currentBearing] = d[tripsKeysRef.interpolateBearing](step)
          d[tripsKeysRef.position] = [d[tripsKeysRef.interpolateLon](step), d[tripsKeysRef.interpolateLat](step)]


          const valid =
            (d[tripsKeysRef.currentVelocity] >= realtime.speedSliderVal[0] &&
              d[tripsKeysRef.currentVelocity] <= realtime.speedSliderVal[1]) &&
            (realtime.currentLines === null || realtime.currentLines.includes(d[tripsKeysRef.lineref]))
          const id_ = d[tripsKeysRef.datedvehiclejourneyref]

          let color = realtime.vizParam === "velocity"
            ? colorScaleCars.current(d[tripsKeysRef.currentVelocity]).replace("rgb(", "[").replace(")", "]")
            : realtime.vizParam === "bearing"
              ? colorScaleBearing.current(d[tripsKeysRef.currentBearing]).replace("rgb(", "[").replace(")", "]")
              : "[" + realtime.operatorColors[d[tripsKeysRef.operatorref]].match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)).toString() + "]"
          color = valid ? color.replace("]", ", 255]") : color.replace("]", ", 0]")
          let speed = 0
          const nextItem = realtimeTrips.data.filter(d =>
            d[tripsKeysRef.datedvehiclejourneyref] === id_
          )

          if (nextItem.length > 0) {


            const n = nextItem[0]
       
            
            const from = point([d[tripsKeysRef.longitude], d[tripsKeysRef.latitude]]);
            const to = point([n[tripsKeysRef.longitude], n[tripsKeysRef.latitude]]);
            const options = { units: 'kilometers' };  // You can also use 'miles', 'meters', etc.
            const options2 = { final: true };
            const dist = distance(from, to, options);
            let az = bearing(from, to, options2)
            az = az === 180 ? n[tripsKeysRef.bearing] : az
            const time = new Date(n[tripsKeysRef.recordedattime]).getTime()
            const prevTime = new Date(d[tripsKeysRef.recordedattime]).getTime()
            const timeDiff = (time - prevTime) / 1000 / 60 / 60
            speed = timeDiff > 0 ? dist / timeDiff : dist > 0.5 ? realtime.timeInterval[realtime.locale] * 1000 : 0
            if (speed < 130 && d[tripsKeysRef.recordedattime] < n[tripsKeysRef.recordedattime]) {
              const interpolateLon = d3.interpolateNumber(d[tripsKeysRef.position][0], n[tripsKeysRef.longitude]);
              const interpolateLat = d3.interpolateNumber(d[tripsKeysRef.position][1], n[tripsKeysRef.latitude]);
              const interpolateVelocity = d3.interpolateNumber(d[tripsKeysRef.velocity],
                realtime.locale === "Israel" ?
                  n[tripsKeysRef.velocity] : speed);
              const interpolateBearing = d[tripsKeysRef.bearing] > 270 && az < 90 ? d3.interpolateNumber(360 - d[tripsKeysRef.bearing],
                az)
                : d[tripsKeysRef.bearing] < 90 && az > 270 ? d3.interpolateNumber(d[tripsKeysRef.bearing], 360 - az)
                  : d3.interpolateNumber(d[tripsKeysRef.bearing], az);

              let ps = Object.keys(tripsKeysRef).map(i => null)
              n.forEach((i, ii) => {
                ps[ii] = i
              })

              ps[tripsKeysRef.route_short_name] = d[tripsKeysRef.route_short_name]
              ps[tripsKeysRef.operatorref] = d[tripsKeysRef.operatorref]
              ps[tripsKeysRef.velocity] = realtime.locale === "Israel" ? n[tripsKeysRef.velocity] : speed
              ps[tripsKeysRef.bearing] = az
              ps[tripsKeysRef.interpolateLon] = interpolateLon
              ps[tripsKeysRef.interpolateLat] = interpolateLat
              ps[tripsKeysRef.interpolateVelocity] = interpolateVelocity
              ps[tripsKeysRef.interpolateBearing] = interpolateBearing
              ps[tripsKeysRef.move] = true
              ps[tripsKeysRef.currentVelocity] = d[tripsKeysRef.currentVelocity]
              ps[tripsKeysRef.currentBearing] = d[tripsKeysRef.currentBearing]
              ps[tripsKeysRef.stage] = "moving"
              ps[tripsKeysRef.color] = color
              ps[tripsKeysRef.valid] = valid
              ps[tripsKeysRef.position] = d[tripsKeysRef.position]


              newData.push(
                ps
              )
            }
            else {


              let ps = [...d]

              ps[tripsKeysRef.move] = false
              ps[tripsKeysRef.stage] = "stagnant"
              ps[tripsKeysRef.color] = color
              ps[tripsKeysRef.valid] = valid

              newData.push(
                ps
              )
            }
          }
          else {
            if (
              new Date(d[tripsKeysRef.recordedattime]).getTime() / 1000 > now - (staleTimeSeconds)
            ) {


              let ps = [...d]
              ps[tripsKeysRef.move] = false
              ps[tripsKeysRef.stage] = "stagnant"
              ps[tripsKeysRef.color] = color
              ps[tripsKeysRef.valid] = valid


              newData.push(
                ps
              )
            }
            else if (

              d[tripsKeysRef.stage] !== "end"
            ) {

              let ps = [...d]

              ps[tripsKeysRef.move] = false
              ps[tripsKeysRef.stage] = "end"
              ps[tripsKeysRef.color] = color
              ps[tripsKeysRef.valid] = valid

              newData.push(
                ps
              )
            }
          }
        })
      }
      realtimeTrips.data.filter(d => !ids.includes(d[tripsKeysRef.datedvehiclejourneyref])
        && new Date(d[tripsKeysRef.recordedattime]).getTime() / 1000 > now - (staleTimeSeconds)
      ).forEach((d) => {

        const routeItem = routeNames.data[d[tripsKeysRef.lineref]]
        
        const shortName = routeItem? (routeNames.data[d[tripsKeysRef.lineref]][realtime.routeNamesKeyRef.route_short_name]).toString(): ""
        const operatorref = routeItem? parseInt(routeNames.data[d[tripsKeysRef.lineref]][realtime.routeNamesKeyRef.operatorref]):0

        const valid =
          (d[tripsKeysRef.velocity] >= realtime.speedSliderVal[0] &&
            d[tripsKeysRef.velocity] <= realtime.speedSliderVal[1]) &&

          (realtime.currentOperators.length === 0 ||
            realtime.currentOperators.includes(d[tripsKeysRef.operatorref]))
        let color = realtime.vizParam === "velocity"
          ? colorScaleCars.current(d[tripsKeysRef.velocity]).replace("rgb(", "[").replace(")", "]")
          : realtime.vizParam === "bearing"
            ? colorScaleBearing.current(d[tripsKeysRef.bearing]).replace("rgb(", "[").replace(")", "]")
            : "[" + realtime.operatorColors[d[tripsKeysRef.operatorref]].match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)).toString() + "]"
        color = valid ? color.replace("]", ", 255]") : color.replace("]", ", 0]")
        const interpolateLon = d3.interpolateNumber(d[tripsKeysRef.longitude], d[tripsKeysRef.longitude]);
        const interpolateLat = d3.interpolateNumber(d[tripsKeysRef.latitude], d[tripsKeysRef.latitude]);
        const interpolateVelocity = d3.interpolateNumber(d[tripsKeysRef.velocity], d[tripsKeysRef.velocity]);
        const interpolateBearing = d3.interpolateNumber(d[tripsKeysRef.bearing], d[tripsKeysRef.bearing]);
        let ps = Object.keys(tripsKeysRef).map(i => null)
        d.forEach((i, ii) => {
          ps[ii] = i
        })
        ps[tripsKeysRef.route_short_name] = shortName
        ps[tripsKeysRef.operatorref] = operatorref
        ps[tripsKeysRef.interpolateLon] = interpolateLon
        ps[tripsKeysRef.interpolateLat] = interpolateLat
        ps[tripsKeysRef.interpolateVelocity] = interpolateVelocity
        ps[tripsKeysRef.interpolateBearing] = interpolateBearing
        ps[tripsKeysRef.move] = false
        ps[tripsKeysRef.currentVelocity] = d[tripsKeysRef.velocity]
        ps[tripsKeysRef.currentBearing] = d[tripsKeysRef.bearing]
        ps[tripsKeysRef.stage] = "start"
        ps[tripsKeysRef.color] = color
        ps[tripsKeysRef.valid] = valid
        ps[tripsKeysRef.position] = [d[tripsKeysRef.longitude], d[tripsKeysRef.latitude]]

        newData.push(
          ps
        )
      })

      setUpdatedData([newData, time, loc])


    }
    // else if (active) {
    //   setUpdatedData([[], new Date().getTime() / 1000, realtime.locname])
    // }
  }, [
    realtimeTrips.data,
    realtime.timeInterval,
    realtime.currentDate,
    realtime.currentLines,
    realtime.currentOperators,
    realtime.locale,
    realtime.speedSliderVal,
    realtime.vizParam,
    realtime.locname,
    realtime.operatorColors,
    realtime.routeNamesKeyRef,
    routeNames.data,
    active,
    mapLocation,
    refreshRate,
    tripsKeysRef,
    staleTimeSeconds,
  ]);
  useEffect(() => {
    animationCounter.current = 0

  }, [updatedData])

  // Average speed and vehicle volume of the hovered road section. The speed feed
  // is keyed by edge_index and stores [avg_speed_kph, volume], either of which
  // can be missing for an edge that has not been reported on yet.
  const hoveredEdgeStats = useMemo(() => {
    if (!edgeHoverInfo || !edgeHoverInfo.object) return null
    const entry = realtimeSpeed.data?.[edgeHoverInfo.object.properties.edge_index]
    return {
      speed: entry && entry[0] !== 'null' ? parseInt(entry[0]) : null,
      volume: entry && entry[1] !== 'null' ? parseInt(entry[1]) : null,
    }
  }, [edgeHoverInfo, realtimeSpeed.data])

  // Vehicle visibility and road network opacity are applied here rather than inside
  // the layer definitions so the control panel takes effect right away: the layers
  // themselves are only rebuilt on the next animation tick (every refreshRate ms).
  const displayLayers = layersRef.current.map((layer) => {
    if (!layer) return layer
    if (!realtime.showVehicles && VEHICLE_LAYER_IDS.includes(layer.id)) {
      return layer.clone({ visible: false })
    }
    if (layer.id === NETWORK_LAYER_ID && realtime.networkOpacity !== 1) {
      return layer.clone({ opacity: realtime.networkOpacity, visible: realtime.networkOpacity > 0 })
    }
    return layer
  })


  return (
    <>
      <div ref={screenRef} >

        {realtime.locname && realtime.locale && <>
          <div style={{ position: 'relative' }}>
            {layersRef.current.length === 0 && (
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <Loader />
              </div>
            )}
            <img src={"speed-legend.png"}
              style={{
                height: '20vh',

                position: "absolute",
                top: '10vh',
                left: 0,
                zIndex: 10,
              }}>
            </img>

            <Map

              ref={mapRef}
              id={!document.fullscreenElement ? "realtime-map" : "realtime-map-full"}
              initialViewState={initialViewState}
              mapStyle={theme.palette.mode === "light" ? "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json" :
                "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"}
              attributionControl={true}
              controller={true}
              maxZoom={18}
              onContextMenu={resetStopSelection}
              onLoad={() =>
                setRTLTextPlugin(
                  'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js',
                  true // lazy-load when RTL text first appears
                )

              }

            >

              {/* <AttributionControl id="monitor-map-attribution" style={{zIndex:999}} customAttribution="Map design by me" />
              <NavigationControl /> */}
              {layersRef.current && (
                <>
                  <DeckGLOverlay layers={displayLayers}
                    controller={true}
                    initialViewState={initialViewState}
                    interleaved
                  >
                  </DeckGLOverlay>
                  <>
                    {
                      updatedData &&
                      updatedData.length > 0 &&
                      updatedData[0].length === 0 &&
                      updatedData[1] !== null &&
                      realtimeTrips.data &&
                      realtimeTrips.data.length > 0 &&
                      realtimeTrips.data.length === 0 && <NoDataMessage />
                    }
                  </>
                  <>
                    {realtime.showVehicles && hoverInfo && hoverInfo.object && hoverInfo.object[tripsKeysRef.valid] && (
                      <div style={{
                        position: 'absolute',
                        zIndex: 99,
                        pointerEvents: 'none',
                        direction: `${pageDir[currLang]}`,
                        border:"2px solid white",
                        opacity:0.7,
                        color:colorIsDark(realtime.operatorColors[routeNames.data[hoverInfo.object[tripsKeysRef.lineref]][realtime.routeNamesKeyRef.operatorref]])?
                          "white":"black"
                        ,
                        backgroundColor: realtime.operatorColors[routeNames.data[hoverInfo.object[tripsKeysRef.lineref]][realtime.routeNamesKeyRef.operatorref]],
                        padding: 5, left: hoverInfo.x + 10, top: hoverInfo.y + 10
                      }}>
                        <b>{transitDataDict.route_short_name[currLang]}</b>: {hoverInfo.object[tripsKeysRef.route_short_name]}
                        <br />
                        {routeNames.data[hoverInfo.object[tripsKeysRef.lineref]][realtime.routeNamesKeyRef.route_long_name]}<br />
                      

                        <b>{transitDataDict.route_id[currLang]}
                        </b>: {hoverInfo.object[tripsKeysRef.lineref]} <br />

                        <b>{transitDataDict.velocity[currLang]}
                        </b>: {parseInt(hoverInfo.object[tripsKeysRef.velocity])} km/h<br />


                        <b>{transitDataDict.recordedattime[currLang]}
                        </b>: {hoverInfo.object[tripsKeysRef.recordedattime]}<br />

                        <b>{transitDataDict.operatorref[currLang]}</b>: {realtime.operatorNames[hoverInfo.object[tripsKeysRef.operatorref]]}<br />

                      </div>
                    )}
                  </>
                  <>
                    {stopHoverInfo && stopHoverInfo.object && (
                      <div style={{
                        position: 'absolute',
                        zIndex: 99,
                        pointerEvents: 'none',
                        direction: `${pageDir[currLang]}`,
                        backgroundColor: "white",
                        padding: 5, left: stopHoverInfo.x + 10, top: stopHoverInfo.y + 10
                      }}>
                        <b>{'Stop Code'}</b>: {stopHoverInfo.object.properties.stop_code}
                        <br />
                        <b>{'Stop Name'}</b>: {stopHoverInfo.object.properties.name}

                      </div>
                    )}
                  </>
                  <>
                    {routeHoverInfo && routeHoverInfo.object && (
                      <div style={{
                        position: 'absolute',
                        zIndex: 99,
                        pointerEvents: 'none',
                        direction: `${pageDir[currLang]}`,
                        border:"2px solid white",
                        opacity:0.7,

                        color:colorIsDark(realtime.operatorColors[routeNames.data[routeHoverInfo.object.properties.route_id][realtime.routeNamesKeyRef.operatorref]])?
                          "white":"black"
                        ,
                        backgroundColor: realtime.operatorColors[routeNames.data[routeHoverInfo.object.properties.route_id][realtime.routeNamesKeyRef.operatorref]],
                        padding: 5, left: routeHoverInfo.x + 10, top: routeHoverInfo.y + 10
                      }}>
                        <b>{routeNames.data[routeHoverInfo.object.properties.route_id][realtime.routeNamesKeyRef.route_short_name]}</b>
                        <br />
                        {routeNames.data[routeHoverInfo.object.properties.route_id][realtime.routeNamesKeyRef.route_long_name]}
                         <br />
                         {realtime.operatorNames[routeNames.data[routeHoverInfo.object.properties.route_id][realtime.routeNamesKeyRef.operatorref]]}
                         <br />
                        {routeHoverInfo.object.properties.route_id}

                      </div>
                    )}
                  </>
                  <>
                    {hoveredEdgeStats && (
                      <div style={{
                        position: 'absolute',
                        zIndex: 99,
                        pointerEvents: 'none',
                        direction: `${pageDir[currLang]}`,
                        backgroundColor: "white",
                        padding: 5, left: edgeHoverInfo.x + 10, top: edgeHoverInfo.y + 10
                      }}>
                        <b>{roadSectionPopup.title[currLang]}</b><br />

                        <b>{roadSectionPopup.avgSpeed[currLang]}
                        </b>: {hoveredEdgeStats.speed !== null ?
                          `${hoveredEdgeStats.speed} km/h` : roadSectionPopup.noData[currLang]}<br />

                        <b>{roadSectionPopup.volume[currLang]}
                        </b>: {hoveredEdgeStats.volume !== null ?
                          hoveredEdgeStats.volume : roadSectionPopup.noData[currLang]}

                      </div>
                    )}
                  </>
                </>
              )}
              <RealTimeProgress
                progress={animationCounter.current}
                scaler={100 / animationCounterInterval.current} />
            </Map>

            <ControlPanel />

          </div>
          <>
            <div style={{ position: "relative", zIndex: 20 }}>
              {document.fullscreenElement ?
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="exit-fullscreen"
                  onClick={toggleFullScreen}
                  sx={{ position: "absolute", top: "20px", right: "20px", }}
                >

                  <FullscreenExitIcon fontSize="large" />
                </IconButton>
                :

                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="enter-fullscreen"

                  onClick={toggleFullScreen}
                  sx={{ position: "absolute", top: "20px", right: "20px" }}
                >

                  <FullscreenIcon fontSize="large" />
                </IconButton>
              }
            </div>
          </>

        </>}
      </div>
    </>
  );
};
export default RealTimeMap;
